import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  ArrowRight, ArrowLeft, CheckCircle, ShieldCheck, IdentificationBadge,
  Baby, Users as UsersIcon, FileText, Heart, Camera as CameraIcon,
  Phone, EnvelopeSimple, Confetti, CalendarBlank, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import SignaturePad from "@/components/SignaturePad";
import PhotoCapture from "@/components/PhotoCapture";
import PhoneInput from "@/components/PhoneInput";
import { NameInput } from "@/lib/validators";
import { Logo } from "@/components/Logo";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const AGE_GROUPS = ["Playgroup", "Nursery", "LKG", "UKG"];
const SOURCES = ["Website", "Referral", "Walk-in", "Google Ads", "Meta Ads", "Event", "WhatsApp"];

const STEPS = [
  { key: "child",   title: "About your child",   icon: Baby },
  { key: "family",  title: "Family details",     icon: UsersIcon },
  { key: "medical", title: "Medical & emergency", icon: Heart },
  { key: "photos",  title: "Photos & ID",         icon: CameraIcon },
  { key: "consent", title: "Consent & signature", icon: FileText },
  { key: "verify",  title: "Verify & submit",    icon: ShieldCheck },
];

export default function AdmissionForm() {
  const [params] = useSearchParams();
  const enquiryIdParam = params.get("enquiry_id");
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [branches, setBranches] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const [data, setData] = useState({
    child_name: "",
    dob: null,                      // Date | null
    age_group: "Nursery",
    gender: "female",
    branch_id: "",
    program: "Full day",
    parent_name: "",
    parent_phone: "",
    parent_email: "",
    parent_occupation: "",
    relationship: "Mother",
    guardian_type: "",
    father_name: "",
    father_phone: "",
    mother_name: "",
    mother_phone: "",
    guardian_name: "",
    guardian_phone: "",
    address: "",
    blood_group: "O+",
    allergies: "",
    conditions: "",
    emergency_name: "",
    emergency_phone: "",
    authorised_pickup: "",
    source: "Website",
    consent_terms: false,
    consent_photo: false,
    consent_medical: false,
  });

  const [files, setFiles] = useState({
    child_photo_file_id: null,
    parent_photo_file_id: null,
    guardian_photo_file_id: null,
    signature_file_id: null,
    documents: [],
  });

  // OTP
  const [otpToken, setOtpToken] = useState(null);
  const [otpDevHint, setOtpDevHint] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);

  const [submittedAdm, setSubmittedAdm] = useState(null);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
  }, []);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const validateStep = (n) => {
    if (n === 0) {
      if (!data.child_name.trim()) return "Child name is required.";
      if (!data.dob) return "Date of birth is required.";
      if (!data.branch_id) return "Please select a branch.";
    }
    if (n === 1) {
      if (!data.parent_name.trim()) return "Parent name is required.";
      if (!data.parent_phone.trim() || data.parent_phone.length < 8) return "Valid phone is required.";
      if (!data.address.trim()) return "Address is required.";
    }
    if (n === 2) {
      if (!data.emergency_name.trim() || !data.emergency_phone.trim()) return "Emergency contact required.";
    }
    if (n === 3) {
      if (!files.child_photo_file_id) return "Please add a photo of your child.";
      if (!files.parent_photo_file_id) return "Please add a photo of the parent.";
    }
    if (n === 4) {
      if (!files.signature_file_id) return "Please sign the consent first.";
      if (!data.consent_terms || !data.consent_photo || !data.consent_medical) return "Please accept all consents to continue.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const sendOtp = async () => {
    if (!data.parent_phone) return toast.error("Enter the parent phone first.");
    setOtpSending(true);
    try {
      const { data: r } = await api.post("/otp/send", { phone: data.parent_phone, purpose: "admission" });
      setOtpToken(r.token);
      if (r.dev_otp) {
        setOtpDevHint(r.dev_otp);
        toast.success(`OTP sent. (Demo mode: ${r.dev_otp})`);
      } else {
        toast.success(`OTP sent to ${data.parent_phone}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send OTP");
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpToken || !otpCode || otpCode.length < 4) return;
    setOtpVerifying(true);
    try {
      await api.post("/otp/verify", { phone: data.parent_phone, otp: otpCode, token: otpToken });
      setOtpVerified(true);
      toast.success("Phone verified!");
    } catch (e) {
      toast.error("Invalid or expired OTP");
    } finally {
      setOtpVerifying(false);
    }
  };

  const submit = async () => {
    if (!otpVerified) return toast.error("Please verify your phone first.");
    try {
      const { data: result } = await api.post("/admissions/full", {
        enquiry_id: enquiryIdParam || undefined,
        branch_id: data.branch_id,
        form_data: {
          child_name: data.child_name,
          dob: data.dob?.toISOString().slice(0, 10),
          age_group: data.age_group,
          gender: data.gender,
          program: data.program,
          parent_name: data.parent_name,
          parent_phone: data.parent_phone,
          parent_email: data.parent_email,
          parent_occupation: data.parent_occupation,
          relationship: data.relationship,
          guardian_type: data.relationship === "Guardian" ? data.guardian_type : null,
          father_name: data.father_name || (data.relationship === "Father" ? data.parent_name : null),
          father_phone: data.father_phone || (data.relationship === "Father" ? data.parent_phone : null),
          mother_name: data.mother_name || (data.relationship === "Mother" ? data.parent_name : null),
          mother_phone: data.mother_phone || (data.relationship === "Mother" ? data.parent_phone : null),
          guardian_name: data.guardian_name,
          guardian_phone: data.guardian_phone,
          address: data.address,
          blood_group: data.blood_group,
          allergies: data.allergies,
          conditions: data.conditions,
          emergency_name: data.emergency_name,
          emergency_phone: data.emergency_phone,
          authorised_pickup: data.authorised_pickup,
          source: data.source,
        },
        signed: true,
        signature_file_id: files.signature_file_id,
        child_photo_file_id: files.child_photo_file_id,
        parent_photo_file_id: files.parent_photo_file_id,
        documents: [
          files.guardian_photo_file_id, ...files.documents,
        ].filter(Boolean),
        otp_token: otpToken,
        otp: otpCode,
        phone: data.parent_phone,
      });
      setSubmittedAdm(result);
      setSubmitted(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit");
    }
  };

  if (submitted) {
    const pdfUrl = submittedAdm
      ? `${process.env.REACT_APP_BACKEND_URL}/api/admissions/${submittedAdm.admission_id}/confirmation.pdf`
      : null;
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center bg-white rounded-3xl border border-border p-10 shadow-floating">
          <div className="w-20 h-20 mx-auto rounded-full bg-sage-light flex items-center justify-center text-sage-deep">
            <Confetti size={40} weight="duotone" />
          </div>
          <h2 className="font-heading text-3xl font-bold mt-4">Admission submitted!</h2>
          <p className="text-ink-soft mt-2">
            Welcome to the MangoCubs family. Your confirmation PDF is ready — keep a copy for your records.
          </p>
          <div className="mt-6 space-y-2">
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="block px-6 py-3 rounded-full bg-terracotta text-white font-semibold shadow-floating"
                data-testid="admission-download-pdf"
              >
                Download confirmation PDF
              </a>
            )}
            <Link to="/" className="block px-6 py-3 rounded-full bg-white border border-border text-ink font-semibold" data-testid="admission-back-home">
              Back to home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-3xl mx-auto px-4 py-8 lg:py-10">
        <Link to="/" className="flex items-center gap-2.5 mb-6" data-testid="adm-brand">
          <Logo size={42} />
        </Link>

        {/* Stepper */}
        <div className="bg-white rounded-3xl border border-border p-4 sm:p-5 mb-5">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex flex-col items-center flex-1 min-w-0" data-testid={`step-${s.key}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs shrink-0 ${
                  i < step ? "bg-sage-deep text-white" : i === step ? "bg-terracotta text-white" : "bg-paper-alt text-ink-soft"
                }`}>
                  {i < step ? <CheckCircle weight="fill" size={18} /> : i + 1}
                </div>
                <div className={`text-[10px] sm:text-xs mt-1.5 text-center truncate w-full ${i === step ? "font-semibold text-ink" : "text-ink-soft"}`}>
                  {s.title}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-border p-5 sm:p-8 shadow-soft">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-2xl bg-terracotta-soft text-terracotta flex items-center justify-center">
              <StepIcon size={22} weight="duotone" />
            </span>
            <h2 className="font-heading text-xl sm:text-2xl font-bold">{STEPS[step].title}</h2>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.25 }}
              data-testid={`step-content-${STEPS[step].key}`}
            >
              {step === 0 && <StepChild data={data} update={update} branches={branches} />}
              {step === 1 && <StepFamily data={data} update={update} />}
              {step === 2 && <StepMedical data={data} update={update} />}
              {step === 3 && <StepPhotos files={files} setFiles={setFiles} />}
              {step === 4 && <StepConsent data={data} update={update} files={files} setFiles={setFiles} />}
              {step === 5 && (
                <StepVerify
                  data={data}
                  files={files}
                  otpToken={otpToken}
                  otpCode={otpCode}
                  setOtpCode={setOtpCode}
                  sendOtp={sendOtp}
                  verifyOtp={verifyOtp}
                  otpSending={otpSending}
                  otpVerifying={otpVerifying}
                  otpVerified={otpVerified}
                  otpDevHint={otpDevHint}
                />
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border bg-white text-ink font-semibold disabled:opacity-40"
              data-testid="adm-back"
            >
              <ArrowLeft weight="bold" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover shadow-floating"
                data-testid="adm-next"
              >
                Continue <ArrowRight weight="bold" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!otpVerified}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sage-deep text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-floating"
                data-testid="adm-submit"
              >
                Submit admission <CheckCircle weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Step components ----------

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-sm font-medium text-ink mb-1.5">{label} {required && <span className="text-terracotta">*</span>}</div>
      {children}
    </label>
  );
}

function Input({ className = "", ...props }) {
  return <input {...props} className={`w-full h-11 px-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta placeholder:text-ink-soft/60 placeholder:font-normal ${className}`} />;
}

function TextArea({ className = "", ...props }) {
  return <textarea {...props} className={`w-full px-3 py-2 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta placeholder:text-ink-soft/60 placeholder:font-normal ${className}`} />;
}

function StepChild({ data, update, branches }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Child's full name" required className="sm:col-span-2">
        <Input value={data.child_name} onChange={(e) => update({ child_name: e.target.value })}
          placeholder="e.g. Aarav Sharma" data-testid="adm-child-name" />
      </Field>
      <Field label="Date of birth" required>
        <DatePickerField value={data.dob} onChange={(d) => update({ dob: d })} />
      </Field>
      <Field label="Gender">
        <Select value={data.gender} onValueChange={(v) => update({ gender: v })}>
          <SelectTrigger data-testid="adm-gender"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="other">Other / prefer not to say</SelectItem></SelectContent>
        </Select>
      </Field>
      <Field label="Age group / Program" required>
        <Select value={data.age_group} onValueChange={(v) => update({ age_group: v })}>
          <SelectTrigger data-testid="adm-age-group"><SelectValue /></SelectTrigger>
          <SelectContent>{AGE_GROUPS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Program type">
        <Select value={data.program} onValueChange={(v) => update({ program: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Half day">Half day</SelectItem>
            <SelectItem value="Full day">Full day</SelectItem>
            <SelectItem value="Extended daycare">Extended daycare</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Preferred branch" required className="sm:col-span-2">
        <Select value={data.branch_id} onValueChange={(v) => update({ branch_id: v })}>
          <SelectTrigger data-testid="adm-branch"><SelectValue placeholder="Choose a centre" /></SelectTrigger>
          <SelectContent>
            {branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name} — {b.city}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function StepFamily({ data, update }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Primary contact name" required>
        <NameInput
          value={data.parent_name}
          onChange={(v) => update({ parent_name: v })}
          testid="adm-parent-name"
          label="parent name"
          placeholder="e.g. Priya Sharma"
        />
      </Field>
      <Field label="Relationship to child" required>
        <Select value={data.relationship} onValueChange={(v) => update({ relationship: v })}>
          <SelectTrigger data-testid="adm-relationship-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Mother">Mother</SelectItem>
            <SelectItem value="Father">Father</SelectItem>
            <SelectItem value="Guardian">Guardian (other relative / legal guardian)</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      {data.relationship === "Guardian" && (
        <Field label="Guardian type" required>
          <Select
            value={data.guardian_type || ""}
            onValueChange={(v) => update({ guardian_type: v })}
          >
            <SelectTrigger data-testid="adm-guardian-type-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="grandfather">Grandfather</SelectItem>
              <SelectItem value="grandmother">Grandmother</SelectItem>
              <SelectItem value="uncle">Uncle</SelectItem>
              <SelectItem value="aunt">Aunt</SelectItem>
              <SelectItem value="elder_sibling">Elder sibling</SelectItem>
              <SelectItem value="legal_guardian">Legal guardian</SelectItem>
              <SelectItem value="other">Other relative</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      )}
      <Field label="Primary contact phone" required>
        <PhoneInput value={data.parent_phone}
          onChange={(v) => update({ parent_phone: v })}
          placeholder="9844139333" testId="adm-parent-phone" />
      </Field>
      <Field label="Primary contact email">
        <Input type="email" value={data.parent_email} onChange={(e) => update({ parent_email: e.target.value })}
          placeholder="priya.sharma@gmail.com" data-testid="adm-parent-email" />
      </Field>
      <Field label="Occupation">
        <Input value={data.parent_occupation} onChange={(e) => update({ parent_occupation: e.target.value })}
          placeholder="e.g. Software Engineer" />
      </Field>

      {/* Wave R3: separate father/mother/guardian if primary contact differs */}
      <div className="sm:col-span-2 mt-2 mb-1 text-xs font-semibold uppercase tracking-wide text-ink-soft">
        Father details {data.relationship === "Father" && <span className="text-mustard-hover">(primary contact)</span>}
      </div>
      <Field label="Father's name">
        <Input
          value={data.father_name || (data.relationship === "Father" ? data.parent_name : "")}
          onChange={(e) => update({ father_name: e.target.value })}
          placeholder="e.g. Rohan Sharma"
          data-testid="adm-father-name"
        />
      </Field>
      <Field label="Father's phone">
        <Input
          value={data.father_phone || (data.relationship === "Father" ? data.parent_phone : "")}
          onChange={(e) => update({ father_phone: e.target.value })}
          placeholder="+91 9844139333"
          data-testid="adm-father-phone"
        />
      </Field>

      <div className="sm:col-span-2 mt-2 mb-1 text-xs font-semibold uppercase tracking-wide text-ink-soft">
        Mother details {data.relationship === "Mother" && <span className="text-mustard-hover">(primary contact)</span>}
      </div>
      <Field label="Mother's name">
        <Input
          value={data.mother_name || (data.relationship === "Mother" ? data.parent_name : "")}
          onChange={(e) => update({ mother_name: e.target.value })}
          placeholder="e.g. Priya Sharma"
          data-testid="adm-mother-name"
        />
      </Field>
      <Field label="Mother's phone">
        <Input
          value={data.mother_phone || (data.relationship === "Mother" ? data.parent_phone : "")}
          onChange={(e) => update({ mother_phone: e.target.value })}
          placeholder="+91 9844139333"
          data-testid="adm-mother-phone"
        />
      </Field>

      <Field label="Secondary guardian name">
        <Input value={data.guardian_name} onChange={(e) => update({ guardian_name: e.target.value })}
          placeholder="e.g. Lakshmi Devi (Grandmother)" />
      </Field>
      <Field label="Secondary guardian phone">
        <Input value={data.guardian_phone} onChange={(e) => update({ guardian_phone: e.target.value })}
          placeholder="+91 9844139333" />
      </Field>
      <Field label="How did you hear about us?">
        <Select value={data.source} onValueChange={(v) => update({ source: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Home address" required className="sm:col-span-2">
        <TextArea rows={3} value={data.address} onChange={(e) => update({ address: e.target.value })}
          placeholder="e.g. 42, 5th Cross, Indiranagar 2nd Stage, Bangalore 560038"
          data-testid="adm-address" />
      </Field>
    </div>
  );
}

function StepMedical({ data, update }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Blood group">
        <Select value={data.blood_group} onValueChange={(v) => update({ blood_group: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{["O+","O-","A+","A-","B+","B-","AB+","AB-"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Allergies">
        <Input value={data.allergies} onChange={(e) => update({ allergies: e.target.value })} placeholder="Peanuts, pollen…" />
      </Field>
      <Field label="Medical conditions" className="sm:col-span-2">
        <TextArea rows={2} value={data.conditions} onChange={(e) => update({ conditions: e.target.value })} placeholder="Anything our teachers should know" />
      </Field>
      <Field label="Emergency contact name" required>
        <Input value={data.emergency_name} onChange={(e) => update({ emergency_name: e.target.value })}
          placeholder="e.g. Lakshmi Devi (Grandmother)" data-testid="adm-emergency-name" />
      </Field>
      <Field label="Emergency contact phone" required>
        <Input value={data.emergency_phone} onChange={(e) => update({ emergency_phone: e.target.value })}
          placeholder="+91 9844139333" data-testid="adm-emergency-phone" />
      </Field>
      <Field label="Authorised pickup persons" className="sm:col-span-2">
        <Input value={data.authorised_pickup} onChange={(e) => update({ authorised_pickup: e.target.value })} placeholder="e.g. Grandmother (Lakshmi), Driver (Arjun)" />
      </Field>
    </div>
  );
}

function StepPhotos({ files, setFiles }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      <div>
        <div className="text-sm font-medium text-ink mb-1.5">Child's live photo <span className="text-terracotta">*</span></div>
        <PhotoCapture
          accept="image/*"
          capture="user"
          publicUpload
          label="Tap to take a photo"
          onUploaded={(id) => setFiles((f) => ({ ...f, child_photo_file_id: id }))}
          testid="adm-child-photo"
        />
      </div>
      <div>
        <div className="text-sm font-medium text-ink mb-1.5">Parent's live photo <span className="text-terracotta">*</span></div>
        <PhotoCapture
          accept="image/*"
          capture="user"
          publicUpload
          label="Parent's photo"
          onUploaded={(id) => setFiles((f) => ({ ...f, parent_photo_file_id: id }))}
          testid="adm-parent-photo"
        />
      </div>
      <div>
        <div className="text-sm font-medium text-ink mb-1.5">Secondary guardian photo</div>
        <PhotoCapture
          accept="image/*"
          capture="user"
          publicUpload
          label="Guardian's photo (optional)"
          onUploaded={(id) => setFiles((f) => ({ ...f, guardian_photo_file_id: id }))}
          testid="adm-guardian-photo"
        />
      </div>
      <div>
        <div className="text-sm font-medium text-ink mb-1.5">Documents (birth certificate, ID etc.)</div>
        <PhotoCapture
          accept="image/*,application/pdf"
          multiple
          publicUpload
          label="Add documents"
          onUploaded={(ids) => setFiles((f) => ({ ...f, documents: ids || [] }))}
          testid="adm-documents"
        />
      </div>
    </div>
  );
}

function StepConsent({ data, update, files, setFiles }) {
  return (
    <div>
      <div className="rounded-2xl bg-paper-alt p-4 text-sm text-ink-soft mb-5 max-h-44 overflow-auto">
        <p>I confirm the details provided are accurate to the best of my knowledge. I authorise MangoCubs to:</p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>Record daily attendance and developmental observations.</li>
          <li>Capture photos / videos of my child during preschool activities for parent communication.</li>
          <li>Administer first-aid in case of minor injury and contact emergency services if required.</li>
          <li>Share the digital admission confirmation, fee receipts and daily updates via the parent dashboard, email and WhatsApp.</li>
        </ul>
      </div>
      <div className="space-y-3 mb-5">
        <Consent label="I accept the terms of admission and fee policy."
          checked={data.consent_terms} onChange={(v) => update({ consent_terms: v })} testid="consent-terms" />
        <Consent label="I consent to photo / video capture for parent communication."
          checked={data.consent_photo} onChange={(v) => update({ consent_photo: v })} testid="consent-photo" />
        <Consent label="I consent to first-aid and medical care in emergencies."
          checked={data.consent_medical} onChange={(v) => update({ consent_medical: v })} testid="consent-medical" />
      </div>
      <SignaturePad onUploaded={(id) => setFiles((f) => ({ ...f, signature_file_id: id }))} label="Sign below to acknowledge" />
    </div>
  );
}

function Consent({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-4 h-4 accent-terracotta" data-testid={testid} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function StepVerify({ data, files, otpToken, otpCode, setOtpCode, sendOtp, verifyOtp, otpSending, otpVerifying, otpVerified, otpDevHint }) {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border p-5">
        <h3 className="font-heading font-semibold mb-3">Review & verify</h3>
        <div className="grid sm:grid-cols-2 gap-y-2 text-sm">
          <Pair k="Child" v={data.child_name || "—"} />
          <Pair k="DOB" v={data.dob ? data.dob.toISOString().slice(0, 10) : "—"} />
          <Pair k="Age group" v={data.age_group} />
          <Pair k="Parent" v={data.parent_name || "—"} />
          <Pair k="Phone" v={data.parent_phone || "—"} />
          <Pair k="Email" v={data.parent_email || "—"} />
        </div>
        <div className="mt-3 text-xs text-ink-soft">
          Documents uploaded: child photo {files.child_photo_file_id ? "✓" : "—"}, parent photo {files.parent_photo_file_id ? "✓" : "—"}, signature {files.signature_file_id ? "✓" : "—"}
        </div>
      </div>

      <div className="rounded-2xl border border-border p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck weight="duotone" className="text-sage-deep" />
          <h3 className="font-heading font-semibold">Verify your phone</h3>
        </div>
        <p className="text-sm text-ink-soft">We'll send a 6-digit code to <span className="font-semibold text-ink">{data.parent_phone || "—"}</span> via SMS and WhatsApp.</p>
        {otpDevHint && (
          <div className="mt-2 text-xs rounded-xl bg-mustard-soft text-mustard-hover p-2">Demo mode — your OTP is <span className="font-mono font-bold">{otpDevHint}</span></div>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" onClick={sendOtp} disabled={otpSending || otpVerified} className="px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold disabled:opacity-60" data-testid="otp-send">
            {otpVerified ? "Verified" : otpSending ? "Sending…" : otpToken ? "Resend OTP" : "Send OTP"}
          </button>
          {otpToken && !otpVerified && (
            <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode} data-testid="otp-input">
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
              </InputOTPGroup>
            </InputOTP>
          )}
          {otpToken && !otpVerified && otpCode.length === 6 && (
            <button type="button" onClick={verifyOtp} disabled={otpVerifying} className="px-4 py-2 rounded-full bg-terracotta text-white text-sm font-semibold disabled:opacity-60" data-testid="otp-verify">
              {otpVerifying ? "Verifying…" : "Verify"}
            </button>
          )}
          {otpVerified && <span className="text-sage-deep text-sm font-semibold inline-flex items-center gap-1"><CheckCircle weight="fill" /> Phone verified</span>}
        </div>
      </div>
    </div>
  );
}

function Pair({ k, v }) {
  return (
    <div className="flex sm:block">
      <div className="text-ink-soft text-xs uppercase tracking-wider sm:mb-0.5 w-28 sm:w-auto">{k}</div>
      <div className="font-medium text-sm">{v}</div>
    </div>
  );
}

function DatePickerField({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const label = value ? value.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "Select date";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full h-11 px-3 rounded-xl border border-border bg-white text-left flex items-center justify-between" data-testid="adm-dob">
          <span className={value ? "text-ink" : "text-ink-soft"}>{label}</span>
          <CalendarBlank size={16} className="text-ink-soft" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          captionLayout="dropdown"
          fromYear={2017}
          toYear={new Date().getFullYear()}
          onSelect={(d) => { onChange(d); setOpen(false); }}
        />
      </PopoverContent>
    </Popover>
  );
}
